import { Skeleton } from 'antd';
import { useState } from 'react';

import { Modal } from '@/components/feedback/modal';
import { useToast } from '@/components/feedback/toast';
import { ApiError } from '@/lib/api/errors';

import { REPLACEMENT_APPROVED_MESSAGE } from '../constants';
import { useApproveReplacement, useReplacement } from '../replacements/hooks';

import styles from './approve-replacement-modal.module.scss';

export type ApproveReplacementModalProps = {
  open: boolean;
  id: string;
  onClose: () => void;
  /** Without-installation approval resolved — clear the overlay and close the drawer. */
  onApproved: () => void;
  /** With-installation chosen — advance to the assign step (request stays Pending). */
  onWithInstallation: () => void;
  /** The footer's Reject button — hands off to the reject flow. */
  onReject: () => void;
};

type Choice = 'without' | 'with';

const CHOICES: { value: Choice; title: string; description: string }[] = [
  {
    value: 'without',
    title: 'Approve Without Installation',
    description: 'Create the replacement order only.',
  },
  {
    value: 'with',
    title: 'Approve With Installation',
    description: 'No installation fee will be charged for this order.',
  },
];

/** Step 1 of the approve flow (Figma 346:284029): states the $0.00 order and — only when the
 *  request has `hasApprovedInstallation` — offers the installation choice as two selectable cards.
 *  Approving without installation mutates here; "With Installation" defers to the assign step,
 *  so the request stays Pending until that resolves (ADR-0019). */
export function ApproveReplacementModal({
  open,
  id,
  onClose,
  onApproved,
  onWithInstallation,
  onReject,
}: ApproveReplacementModalProps) {
  const { data: detail, isPending } = useReplacement(open ? id : '');
  const [choice, setChoice] = useState<Choice>('without');
  const approve = useApproveReplacement(id);
  const toast = useToast();

  const onPrimary = () => {
    if (choice === 'with') {
      onWithInstallation();
      return;
    }
    approve.mutate(
      { withInstallation: false },
      {
        onSuccess: () => {
          toast.success({ title: REPLACEMENT_APPROVED_MESSAGE });
          onApproved();
          onClose();
        },
        onError: (e) =>
          toast.error({
            title: e instanceof ApiError ? e.message : 'Could not approve. Please try again.',
          }),
      },
    );
  };

  return (
    <Modal
      open={open}
      onClose={onClose}
      // Figma 346:284029 card is 733px — wider than the `lg` (709px) preset.
      width={733}
      centered={false}
      title={
        <div className={styles.header}>
          <h3 className={styles.title}>Approve Replacement Request</h3>
          <p className={styles.lede}>
            Approving this request will create a new replacement order with an order amount of{' '}
            <strong className={styles.amount}>$0.00.</strong>
          </p>
        </div>
      }
      primaryText="Approve"
      onPrimary={onPrimary}
      primaryDisabled={isPending}
      cancelText="Reject"
      onCancel={onReject}
      loading={approve.isPending}
    >
      {isPending || !detail ? (
        <Skeleton active paragraph={{ rows: 2 }} />
      ) : detail.hasApprovedInstallation ? (
        <div className={styles.body}>
          <p className={styles.question}>
            Do you want to create an installation request for this replacement order?
          </p>
          <div className={styles.choices}>
            {CHOICES.map((c) => (
              <button
                key={c.value}
                type="button"
                aria-pressed={choice === c.value}
                className={`${styles.choice} ${choice === c.value ? styles.choiceSelected : ''}`}
                onClick={() => setChoice(c.value)}
              >
                <span className={styles.choiceTitle}>{c.title}</span>
                <span className={styles.choiceDescription}>{c.description}</span>
              </button>
            ))}
          </div>
        </div>
      ) : null}
    </Modal>
  );
}
